import clsx from 'clsx';
import { useEffect } from 'react';
import { LoadingTracker } from 'src/components/common/LoadingTracker';
import { useAppContext } from 'src/contexts/AppContext';
import { vars } from 'src/core/themes';
import { Content, Stack } from 'src/core/ui';
import { ReleaseNoteDisplayDialog } from 'src/dialogs/ReleaseNoteDisplayDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { usePageLoadStatus } from 'src/hooks/usePageLoadStatus';
import { usePosLiteAccount } from 'src/hooks/usePosLiteAccount';
import { useReleaseNotes } from 'src/hooks/useReleaseNotes';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ALL_INVENTORY_VIEW_PERMISSIONS,
  ALL_SALES_VIEW_PERMISSIONS,
  hasAccessToRoute,
} from 'src/utils/userUtils';
import { Feature, Permission } from 'src/WebApiController';

import { ActivitySection } from '../Home/ActivitySection';
import * as styles from '../Home/Home.css';
import { MetricsSection } from '../Home/MetricsSection';
import { NotificationsSection } from '../Home/NotificationsSection';
import { RecentSalesSectionV2 } from '../Home/RecentSalesSection/RecentSalesSectionV2';
import { MainRoute } from '../MainRoute';
import { DashboardSectionWrapper } from './DashboardSectionWrapper';
import { DataInsightsChartSection } from './DataInsightsChart/DataInsightsChartSection';
import { SponsoredRevenueHomeWidget } from './SponsoredRevenueHomeWidget';

export const Dashboard = () => {
  const { loginContext, appContext } = useAppContext();
  const { markComponentLoaded } = usePageLoadStatus();
  const hasHomePageActivityLogFeature = useUserHasFeature(
    Feature.HomePageActivityLog
  );
  const hasViewSalesPermission = useUserHasAnyOfPermissions(
    ...ALL_SALES_VIEW_PERMISSIONS
  );
  const hasViewListingsPermission = useUserHasAnyOfPermissions(
    ...ALL_INVENTORY_VIEW_PERMISSIONS
  );

  const hasViewSalesDataPermission = useUserHasAnyOfPermissions(
    Permission.Sales_ViewHomePageMetrics
  );

  const isPosLite = usePosLiteAccount();

  const hasNotificationsAccess = hasAccessToRoute(
    loginContext?.user,
    appContext?.features,
    MainRoute.Notifications
  );

  const {
    releaseNoteInfos: releaseNotesUnread,
    setReleaseNoteIdsRead,
    releaseNoteIdsRead,
  } = useReleaseNotes({ getUnreadOnly: true });
  const releaseNotesDialog = useBasicDialog();

  useEffect(() => {
    // Mark LayoutContent as loaded immediately since it renders synchronously
    markComponentLoaded('LayoutContent');
  }, [markComponentLoaded]);

  useEffect(() => {
    if (releaseNotesUnread && releaseNotesUnread.length > 0) {
      if (!releaseNotesDialog.dialogProps.isOpen) {
        releaseNotesDialog.launchDialog();
      }
    } else {
      if (releaseNotesDialog.dialogProps.isOpen) {
        releaseNotesDialog.closeDialog();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseNotesUnread]);

  return (
    <LayoutContent mainRoute={MainRoute.Home}>
      <Stack
        direction="column"
        style={{ padding: '28px 0' }}
        className={clsx([styles.root])}
      >
        {hasViewListingsPermission && (
          <LoadingTracker componentName="MetricsSection">
            <DashboardSectionWrapper>
              <MetricsSection variant={'dashboard'} />
            </DashboardSectionWrapper>
          </LoadingTracker>
        )}

        {hasViewSalesDataPermission && (
          <>
            {isPosLite && (
              <div className={styles.mobileOnly}>
                <LoadingTracker componentName="SponsoredRevenueHomeWidget">
                  <SponsoredRevenueHomeWidget />
                </LoadingTracker>
              </div>
            )}
            <div className={isPosLite ? styles.desktopOnly : undefined}>
              <LoadingTracker componentName="DataInsightsChartSection">
                <DataInsightsChartSection />
              </LoadingTracker>
            </div>
          </>
        )}
        <Stack style={{ marginTop: vars.spacing.xxl }} direction="column">
          {hasViewSalesPermission && (
            <LoadingTracker componentName="RecentSalesSection">
              <RecentSalesSectionV2 />
            </LoadingTracker>
          )}

          <DashboardSectionWrapper fixed>
            <Stack
              gap="xl"
              flexWrap="wrap"
              style={{ marginTop: vars.spacing.xxl }}
            >
              {hasHomePageActivityLogFeature && (
                <LoadingTracker componentName="ActivitySection">
                  <div className={styles.sectionWrapper}>
                    <ActivitySection />
                  </div>
                </LoadingTracker>
              )}
              {hasNotificationsAccess && (
                <LoadingTracker componentName="NotificationsSection">
                  <div className={styles.sectionWrapper}>
                    <NotificationsSection />
                  </div>
                </LoadingTracker>
              )}
            </Stack>
          </DashboardSectionWrapper>
        </Stack>
      </Stack>
      {Boolean(releaseNotesUnread?.length) && (
        <ReleaseNoteDisplayDialog
          {...releaseNotesDialog.dialogProps}
          releaseNoteInfos={releaseNotesUnread}
          headerText={<Content id={ContentId.DeletePurchaseVendorAccount} />}
          bodyText={
            <Content id={ContentId.DeletePurchaseVendorAccountWarning} />
          }
          onReleaseNotesRead={async () => {
            await setReleaseNoteIdsRead([
              ...(releaseNoteIdsRead ?? []),
              ...(releaseNotesUnread?.map((rn) => rn.id) ?? []),
            ]);
            releaseNotesDialog.closeDialog();
          }}
        />
      )}
    </LayoutContent>
  );
};
